/**
 * Cilësimet e sistemit – Gjuha, Dark/Light mode, Ndrysho fjalëkalimin, Njoftimet.
 * Web: dy kolona – lista majtas; Change Password / Notification paneli djathtas (i njëjti shape). Notification: lista gjysma-gjysma (dy kolona). Back fshihet në web.
 * Mobile: një kolonë, Change Password dhe Notification lidhen te faqet e tyre.
 */
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { BACKEND_URL } from '../config';

const THEME_KEY = 'dio-theme';

// Renditja: kolona 1 = L1, L2, L3, DO1–DO4, Temperatura, Lagështia; kolona 2 = DIN1–DIN8
const NOTIFICATION_ROWS_COL1 = [
  { key: 'l1', label: 'L1' }, { key: 'l2', label: 'L2' }, { key: 'l3', label: 'L3' },
  { key: 'do0', label: 'DO1' }, { key: 'do1', label: 'DO2' }, { key: 'do2', label: 'DO3' }, { key: 'do3', label: 'DO4' },
  { key: 'temp', label: 'Temperatura' }, { key: 'humidity', label: 'Lagështia' },
];
const NOTIFICATION_ROWS_COL2 = Array.from({ length: 8 }, (_, i) => ({ key: `din${i + 1}`, label: `DIN${i + 1}` }));
const NOTIFICATION_EVENT_ROWS = [...NOTIFICATION_ROWS_COL1, ...NOTIFICATION_ROWS_COL2];
const defaultNotifSettings = () =>
  Object.fromEntries(NOTIFICATION_EVENT_ROWS.map((r) => [r.key, { push: false, email: false }]));

function ToggleSwitch({ value, onChange }) {
  return (
    <button type="button" role="switch" aria-checked={value} onClick={() => onChange(!value)}
      className={`w-10 h-5 rounded-full shrink-0 transition-colors relative ${value ? 'bg-green-500' : 'bg-red-500'}`}>
      <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200" style={{ left: value ? '22px' : '2px' }} />
    </button>
  );
}

const MeSettings = () => {
  const { user } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const allowedPaths = user?.allowed_routes && Array.isArray(user.allowed_routes) && user.allowed_routes.length > 0 ? user.allowed_routes : null;
  const canShowNotifications = allowedPaths === null || allowedPaths.includes('/me/notifications');
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const [theme, setThemeState] = useState(() => {
    if (typeof document === 'undefined') return 'dark';
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  });
  const [activePanel, setActivePanel] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [notifDevices, setNotifDevices] = useState([]);
  const [notifDeviceId, setNotifDeviceId] = useState('');
  const [notifSettings, setNotifSettings] = useState(defaultNotifSettings());
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);
  const [showNotifDeviceDropdown, setShowNotifDeviceDropdown] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(THEME_KEY) || 'dark';
    setThemeState(saved);
  }, []);

  const setTheme = (value) => {
    setThemeState(value);
    localStorage.setItem(THEME_KEY, value);
    if (value === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // ESC = back to /me
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') navigate('/me'); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const getToken = () => localStorage.getItem('access_token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const fetchNotifDevices = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/s475/devices`, { headers: headers() });
      const list = Array.isArray(res.data) ? res.data : [];
      setNotifDevices(list);
      setNotifDeviceId((prev) => (list.length > 0 && !prev ? list[0].id : prev));
    } catch (e) {
      setNotifDevices([]);
    } finally {
      setNotifLoading(false);
    }
  }, []);
  const fetchNotifSettings = useCallback(async () => {
    if (!notifDeviceId) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/notification-settings?device_id=${encodeURIComponent(notifDeviceId)}`, { headers: headers() });
      const s = res.data?.settings || {};
      setNotifSettings((prev) => {
        const next = { ...defaultNotifSettings() };
        NOTIFICATION_EVENT_ROWS.forEach((r) => {
          if (s[r.key]) next[r.key] = { push: !!s[r.key].push, email: !!s[r.key].email };
        });
        return next;
      });
    } catch (e) {
      setNotifSettings(defaultNotifSettings());
    }
  }, [notifDeviceId]);
  useEffect(() => {
    fetchNotifDevices();
  }, [fetchNotifDevices]);
  useEffect(() => {
    if (notifDeviceId) fetchNotifSettings();
    else setNotifSettings(defaultNotifSettings());
  }, [notifDeviceId, fetchNotifSettings]);
  const setNotifToggle = (eventKey, channel, value) => {
    setNotifSettings((prev) => ({ ...prev, [eventKey]: { ...prev[eventKey], [channel]: value } }));
  };
  const handleNotifSave = async () => {
    if (!notifDeviceId) {
      toast.error(t('selectDevice'));
      return;
    }
    setNotifSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/notification-settings`, { device_id: notifDeviceId, settings: notifSettings }, { headers: headers() });
      toast.success('Preferencat e njoftimeve u ruajtën.');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || t('errorSaving'));
    } finally {
      setNotifSaving(false);
    }
  };
  const handleTestEmail = async () => {
    setTestEmailLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/notification-settings/test-email`, {}, { headers: headers() });
      toast.success(res.data?.message || 'Email test u dërgua.');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || t('testEmailFailed'));
    } finally {
      setTestEmailLoading(false);
    }
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword.trim()) {
      toast.error(t('currentPasswordRequired'));
      return;
    }
    if (!newPassword.trim() || newPassword.length < 4) {
      toast.error(t('newPasswordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('passwordsDoNotMatch'));
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: headers() }
      );
      toast.success(t('passwordChangedSuccess'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setActivePanel(null);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || t('errorGeneric');
      toast.error(typeof msg === 'string' ? msg : t('passwordChangeError'));
    } finally {
      setPasswordLoading(false);
    }
  };

  /* Shape i njëjtë për të gjitha (si Notification): #1E293B, rounded-xl, border */
  const rowClass = 'flex items-center justify-between w-full px-4 py-2.5 rounded-xl bg-white dark:bg-[#1E293B] border border-gray-200 dark:border-[#334155] text-gray-900 dark:text-gray-100 text-sm md:text-base';
  const cardClass = 'rounded-xl bg-white dark:bg-[#1E293B] border border-gray-200 dark:border-[#334155] text-gray-900 dark:text-gray-100';
  const iconClass = 'w-5 h-5 shrink-0 text-gray-500 dark:text-gray-400 mr-3';
  const toggleBtnClass = 'px-2 py-1 rounded-md text-xs font-medium';

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-[#0f172a] pb-24 md:pb-6">
        <div className="px-4 pt-4 md:flex md:gap-6 md:items-start md:max-w-[67.2rem] md:mr-auto md:ml-4">
          {/* Kolona e majtë: titulli + lista e cilësimeve */}
          <div className="max-w-lg md:w-[13cm] md:min-w-[13cm] md:max-w-[13cm] md:flex-shrink-0">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h1 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white">{t('systemSettings')}</h1>
              <Link to="/me" className="md:hidden inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-sm md:text-base">
                <span>{t('back')}</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
            </div>

            <div className="space-y-2">
              <div className={rowClass}>
                <div className="flex items-center flex-1 min-w-0">
                  <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                  <span className="truncate">{t('languageLabel')}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button type="button" onClick={() => setLanguage('sq')} className={`${toggleBtnClass} ${language === 'sq' ? 'bg-blue-600 text-white' : 'bg-gray-500/50 dark:bg-gray-600 text-gray-600 dark:text-gray-400'}`}>Shqip</button>
                  <button type="button" onClick={() => setLanguage('en')} className={`${toggleBtnClass} ${language === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-500/50 dark:bg-gray-600 text-gray-600 dark:text-gray-400'}`}>English</button>
                </div>
              </div>

              <div className={rowClass}>
                <div className="flex items-center flex-1 min-w-0">
                  <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                  <span className="truncate">{t('darkLightMode')}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button type="button" onClick={() => setTheme('dark')} className={`${toggleBtnClass} ${theme === 'dark' ? 'bg-gray-600 dark:bg-gray-500 text-white' : 'bg-gray-500/50 dark:bg-gray-600 text-gray-600 dark:text-gray-400'}`}>{t('darkMode')}</button>
                  <button type="button" onClick={() => setTheme('light')} className={`${toggleBtnClass} ${theme === 'light' ? 'bg-yellow-500 text-gray-900' : 'bg-gray-500/50 dark:bg-gray-600 text-gray-600 dark:text-gray-400'}`}>{t('lightMode')}</button>
                </div>
              </div>

              {/* Change Password: mobile = Link, web = buton që hap panelin djathtas */}
              <Link to="/me/change-password" className={`${rowClass} flex md:hidden`}>
                <div className="flex items-center flex-1 min-w-0">
                  <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <span className="truncate">{t('changePassword')}</span>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>
              <button type="button" onClick={() => setActivePanel(activePanel === 'change-password' ? null : 'change-password')} className={`${rowClass} w-full text-left hidden md:flex ${activePanel === 'change-password' ? 'ring-2 ring-blue-500' : ''}`}>
                <div className="flex items-center flex-1 min-w-0">
                  <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <span className="truncate">{t('changePassword')}</span>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>

              {canShowNotifications && (
                <>
                  <Link to="/me/notifications" className={`${rowClass} flex md:hidden`}>
                    <div className="flex items-center flex-1 min-w-0">
                      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <span className="truncate">{t('notification')}</span>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </Link>
                  <button type="button" onClick={() => setActivePanel(activePanel === 'notification' ? null : 'notification')} className={`${rowClass} w-full text-left hidden md:flex ${activePanel === 'notification' ? 'ring-2 ring-blue-500' : ''}`}>
                    <div className="flex items-center flex-1 min-w-0">
                      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <span className="truncate">{t('notification')}</span>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </>
              )}

              {isSuperAdmin && (
                <Link to="/me/konfigurimi" className={`${rowClass} flex md:hidden`}>
                  <div className="flex items-center flex-1 min-w-0">
                    <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="truncate">{t('konfigurimi')}</span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </Link>
              )}
            </div>
          </div>

          {/* Kolona e djathtë (vetëm web): Change Password ose Notification – i njëjti shape */}
          {activePanel === 'change-password' && (
            <div className={`${cardClass} p-4 w-full max-w-md md:max-w-[33.6rem] mt-4 md:mt-0 flex-shrink-0`}>
              <h2 className="text-lg font-semibold mb-4">{t('changePassword')}</h2>
              <form onSubmit={handleChangePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('currentPasswordLabel')}</label>
                  <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" placeholder="••••••••" autoComplete="current-password" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('newPasswordLabel')}</label>
                  <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" placeholder="••••••••" autoComplete="new-password" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('confirmNewPasswordLabel')}</label>
                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" placeholder="••••••••" autoComplete="new-password" />
                </div>
                <button type="submit" disabled={passwordLoading} className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium">
                  {passwordLoading ? t('sending') : t('save')}
                </button>
              </form>
            </div>
          )}

          {/* Panel Njoftimet (web): shfaqet vetëm nëse superadmini e ka lejuar në Navigimi i lejuar */}
          {canShowNotifications && activePanel === 'notification' && (
            <div className={`${cardClass} p-4 w-full max-w-2xl md:max-w-[50.4rem] mt-4 md:mt-0 flex-shrink-0`}>
              <h2 className="text-lg font-semibold mb-2">{t('notificationsTitle')}</h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{t('notificationSelectDeviceHint')}</p>
              <button type="button" onClick={handleTestEmail} disabled={testEmailLoading} className="text-sm text-cyan-600 dark:text-cyan-400 hover:underline disabled:opacity-50 mb-3">
                {testEmailLoading ? t('sending') : t('testEmail')}
              </button>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('deviceLabel')}</label>
                <div className="relative">
                  <button type="button" onClick={() => setShowNotifDeviceDropdown(!showNotifDeviceDropdown)} className="flex items-center justify-between w-full px-4 py-2.5 rounded-xl bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-left text-sm">
                    <span className="truncate">{notifDevices.find((d) => String(d.id) === String(notifDeviceId))?.location_name || notifDevices.find((d) => String(d.id) === String(notifDeviceId))?.label || notifDeviceId || t('selectDevice')}</span>
                    <svg className={`w-4 h-4 shrink-0 transition-transform ${showNotifDeviceDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  {showNotifDeviceDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white dark:bg-[#1e293b] rounded-xl shadow-lg z-50 border border-gray-200 dark:border-[#334155]">
                      {notifDevices.map((d) => (
                        <button key={d.id} type="button" onClick={() => { setNotifDeviceId(d.id); setShowNotifDeviceDropdown(false); }} className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] ${String(notifDeviceId) === String(d.id) ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 font-medium' : 'text-gray-700 dark:text-white'}`}>
                          {d.location_name || d.label || d.id}
                        </button>
                      ))}
                      {notifDevices.length === 0 && !notifLoading && <div className="px-4 py-3 text-gray-500 text-sm">{t('noDevices')}</div>}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                <div className="space-y-1">
                  {NOTIFICATION_ROWS_COL1.map((row) => (
                    <div key={row.key} className="flex items-center justify-between px-2 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate pr-2">{row.label}</span>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{t('push')}</span>
                          <ToggleSwitch value={notifSettings[row.key]?.push} onChange={(v) => setNotifToggle(row.key, 'push', v)} />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{t('emailNotif')}</span>
                          <ToggleSwitch value={notifSettings[row.key]?.email} onChange={(v) => setNotifToggle(row.key, 'email', v)} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="space-y-1">
                  {NOTIFICATION_ROWS_COL2.map((row) => (
                    <div key={row.key} className="flex items-center justify-between px-2 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate pr-2">{row.label}</span>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{t('push')}</span>
                          <ToggleSwitch value={notifSettings[row.key]?.push} onChange={(v) => setNotifToggle(row.key, 'push', v)} />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{t('emailNotif')}</span>
                          <ToggleSwitch value={notifSettings[row.key]?.email} onChange={(v) => setNotifToggle(row.key, 'email', v)} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button type="button" onClick={() => setActivePanel(null)} className="flex-1 py-2.5 rounded-xl bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">
                  {t('cancel')}
                </button>
                <button type="button" onClick={handleNotifSave} disabled={notifSaving} className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60">
                  {notifSaving ? t('saving') : t('saveNotifications')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default MeSettings;
